/**
 * PartBundleFormDialog — create or edit a part bundle (#3350 phase 1).
 *
 * A bundle is a name, an optional description and 1–50 catalog parts with a quantity
 * each. It stores no price. Saving sends the whole item list in order; the server
 * refuses the request as a whole if any part is missing or deactivated, so a failed
 * save never leaves a half-edited bundle behind.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotify } from '@/hooks/useNotify';
import {
  useCreatePartBundle,
  useInventoryParts,
  usePartBundle,
  useUpdatePartBundle,
  type PartBundleInput,
  type PartListItem,
} from '@/hooks/useInventory';
import { describeApiError } from '@/lib/describeApiError';

/** Mirrors the API and the database: at most 50 parts, quantity 0.01–9999.99. */
export const PART_BUNDLE_MAX_ITEMS = 50;
export const PART_BUNDLE_MAX_QUANTITY = 9999.99;

/** Quantity typed by the user ("2", "2,5", "0.25"), or null when it is not a valid bundle quantity. */
export function parseBundleQuantity(text: string): number | null {
  const normalized = text.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const quantity = Number(normalized);
  return quantity > 0 && quantity <= PART_BUNDLE_MAX_QUANTITY ? quantity : null;
}

interface ItemRow {
  key: string;
  partId: string;
  label: string;
  isActive: boolean;
  quantityText: string;
}

export interface PartBundleFormDialogProps {
  open: boolean;
  /** `null` creates a new bundle. */
  bundleId: string | null;
  onClose: () => void;
}

export function PartBundleFormDialog({ open, bundleId, onClose }: PartBundleFormDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isEdit = bundleId !== null;

  const detail = usePartBundle(open && bundleId ? bundleId : undefined);
  const createBundle = useCreatePartBundle();
  const updateBundle = useUpdatePartBundle();
  const saving = createBundle.isPending || updateBundle.isPending;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rows, setRows] = useState<ItemRow[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [partQuery, setPartQuery] = useState('');
  const [duplicateNotice, setDuplicateNotice] = useState(false);
  const hydratedFor = useRef<string | null>(null);

  // Start every opening from a clean form; an edit is filled in once its bundle has loaded.
  useEffect(() => {
    if (!open) {
      hydratedFor.current = null;
      return;
    }
    if (bundleId === null && hydratedFor.current !== 'new') {
      hydratedFor.current = 'new';
      setName('');
      setDescription('');
      setRows([]);
      setSubmitted(false);
      setPartQuery('');
      setDuplicateNotice(false);
    }
  }, [open, bundleId]);

  useEffect(() => {
    if (!open || !bundleId || !detail.data || hydratedFor.current === bundleId) return;
    hydratedFor.current = bundleId;
    setName(detail.data.name);
    setDescription(detail.data.description ?? '');
    setRows(
      detail.data.items.map((item) => ({
        key: item.id,
        partId: item.partId,
        label: `${item.name} — ${item.sku}`,
        isActive: item.isActive,
        quantityText: String(item.quantity),
      })),
    );
    setSubmitted(false);
    setPartQuery('');
    setDuplicateNotice(false);
  }, [open, bundleId, detail.data]);

  const debouncedQuery = useDebounce(partQuery.trim(), 250);
  const partSearch = useInventoryParts(
    { q: debouncedQuery, limit: 20, isActive: true },
    { enabled: open && debouncedQuery.length > 0 },
  );
  const options = debouncedQuery === partQuery.trim() && debouncedQuery.length > 0
    ? (partSearch.data?.pages.flatMap((page) => page.data) ?? [])
    : [];

  const addPart = (part: PartListItem) => {
    if (rows.some((row) => row.partId === part.id)) {
      setDuplicateNotice(true);
      return;
    }
    setDuplicateNotice(false);
    setRows((prev) => [
      ...prev,
      {
        key: crypto.randomUUID(),
        partId: part.id,
        label: `${part.name} — ${part.sku}`,
        isActive: true,
        quantityText: '1',
      },
    ]);
  };

  const updateQuantity = (key: string, quantityText: string) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, quantityText } : row)));
  const removeRow = (key: string) => {
    setDuplicateNotice(false);
    setRows((prev) => prev.filter((row) => row.key !== key));
  };

  const nameError = name.trim() === '' ? 'bundles.form.nameRequired' : null;
  const itemsError =
    rows.length === 0
      ? 'bundles.form.itemsRequired'
      : rows.length > PART_BUNDLE_MAX_ITEMS
        ? 'bundles.form.itemsTooMany'
        : rows.some((row) => !row.isActive)
          ? 'bundles.form.removeUnavailable'
          : null;
  const hasBadQuantity = rows.some((row) => parseBundleQuantity(row.quantityText) === null);
  const invalid = nameError !== null || itemsError !== null || hasBadQuantity;

  const failureMessage = (error: unknown): string => {
    const status = (error as { status?: unknown } | null)?.status;
    const body = (error as { data?: { partIds?: unknown } } | null)?.data;
    if (status === 409) return t('bundles.errors.nameTaken');
    if (status === 400 && Array.isArray(body?.partIds)) return t('bundles.errors.partsUnavailable');
    return describeApiError(error, tc, { fallback: t('bundles.errors.saveFailed') });
  };

  const handleSave = () => {
    setSubmitted(true);
    if (invalid) return;
    const trimmedDescription = description.trim();
    const items = rows.map((row) => ({
      partId: row.partId,
      quantity: parseBundleQuantity(row.quantityText) ?? 0,
    }));
    const onError = (error: unknown) => notify.error(tc('error'), failureMessage(error));
    if (isEdit && bundleId) {
      updateBundle.mutate(
        { id: bundleId, data: { name: name.trim(), description: trimmedDescription === '' ? null : trimmedDescription, items } },
        {
          onSuccess: () => {
            notify.success(t('bundles.notify.updated'), t('bundles.notify.updatedDesc', { name: name.trim() }));
            onClose();
          },
          onError,
        },
      );
      return;
    }
    const data: PartBundleInput = { name: name.trim(), items };
    if (trimmedDescription !== '') data.description = trimmedDescription;
    createBundle.mutate(data, {
      onSuccess: () => {
        notify.success(t('bundles.notify.created'), t('bundles.notify.createdDesc', { name: name.trim() }));
        onClose();
      },
      onError,
    });
  };

  const loadingEdit = isEdit && detail.isLoading;

  return (
    <ResponsiveDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      mobileTitle={isEdit ? t('bundles.form.editTitle') : t('bundles.form.createTitle')}
      aria-labelledby="part-bundle-form-title"
    >
      <DialogTitle id="part-bundle-form-title">
        {isEdit ? t('bundles.form.editTitle') : t('bundles.form.createTitle')}
      </DialogTitle>
      <DialogContent>
        {loadingEdit ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label={tc('loading')} />
          </Box>
        ) : isEdit && detail.isError ? (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => void detail.refetch()}>{t('bundles.retry')}</Button>}>
            {t('bundles.loadFailed')}
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('bundles.form.name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              error={submitted && nameError !== null}
              helperText={submitted && nameError ? t(nameError) : t('bundles.form.nameHint')}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              required
              fullWidth
            />
            <TextField
              label={t('bundles.form.description')}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              multiline
              minRows={2}
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('bundles.form.items')}
              </Typography>
              {rows.map((row) => (
                <Box key={row.key} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, minWidth: 0 }}
                    color={row.isActive ? 'text.primary' : 'error'}
                  >
                    {row.label}
                    {!row.isActive && ` (${t('bundles.form.unavailable')})`}
                  </Typography>
                  <TextField
                    size="small"
                    sx={{ width: 90, flexShrink: 0 }}
                    value={row.quantityText}
                    onChange={(event) => updateQuantity(row.key, event.target.value)}
                    error={submitted && parseBundleQuantity(row.quantityText) === null}
                    slotProps={{
                      htmlInput: { inputMode: 'decimal', 'aria-label': t('bundles.form.quantityFor', { name: row.label }) },
                    }}
                  />
                  <Tooltip title={t('bundles.form.removeItem')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeRow(row.key)}
                      aria-label={t('bundles.form.removeItemNamed', { name: row.label })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              {rows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                  {t('bundles.form.noItems')}
                </Typography>
              )}
              {submitted && hasBadQuantity && (
                <Typography variant="caption" color="error" component="div" sx={{ mb: 1 }}>
                  {t('bundles.form.quantityInvalid', { max: PART_BUNDLE_MAX_QUANTITY })}
                </Typography>
              )}
              {submitted && itemsError && (
                <Typography variant="caption" color="error" component="div" sx={{ mb: 1 }}>
                  {t(itemsError, { count: PART_BUNDLE_MAX_ITEMS })}
                </Typography>
              )}
              {duplicateNotice && (
                <Typography variant="caption" color="warning.main" component="div" sx={{ mb: 1 }}>
                  {t('bundles.form.duplicatePart')}
                </Typography>
              )}
              <Autocomplete
                size="small"
                options={options}
                value={null}
                inputValue={partQuery}
                filterOptions={(items) => items}
                getOptionLabel={(option) => `${option.name} — ${option.sku}`}
                getOptionKey={(option) => option.id}
                loading={partSearch.isFetching}
                disabled={rows.length >= PART_BUNDLE_MAX_ITEMS}
                onInputChange={(_, input, reason) => {
                  if (reason === 'input' || reason === 'clear') setPartQuery(input);
                }}
                onChange={(_, part) => {
                  if (part) {
                    addPart(part);
                    setPartQuery('');
                  }
                }}
                noOptionsText={t('bundles.form.noPartsFound')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('bundles.form.addPart')}
                    placeholder={t('bundles.form.searchPart')}
                    helperText={rows.length >= PART_BUNDLE_MAX_ITEMS ? t('bundles.form.itemsTooMany', { count: PART_BUNDLE_MAX_ITEMS }) : undefined}
                  />
                )}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loadingEdit || (isEdit && detail.isError)}>
          {saving ? <CircularProgress size={20} aria-label={tc('loading')} /> : tc('save')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
